import { observer } from "mobx-react"; // Import observer from mobx-react for state management
import moment from "moment"; // Import moment for date manipulation
import React, { useCallback, useMemo } from "react"; // Import React and hooks
import toast from "react-hot-toast"; // Import toast for notifications
import {
  getOverlappingBreaksByRange, // Import function to get overlapping breaks
  isCurrentDateGreater, // Import function to check if current date is greater
  isCurrentDateTimeGreater, // Import function to check if current date and time is greater
} from "../../../../constants/constants"; // Import constants
import bookingStatusEnum from "../../../../constants/enums/booking-status-enum"; // Import booking status enum
import EventTypeEnum from "../../../../constants/enums/event-type-enum"; // Import event type enum
import { useStore } from "../../../../contexts/store-provider"; // Import custom hook for store context
import { IOption } from "../../../../models/ICommon"; // Import IOption interface
import { formatMessage } from "../../../../translations/format-message"; // Import function to format messages
import DroppableCell from "../droppableEvent"; // Import DroppableCell component
import AddBreakDetailsPopupHOC from "../hoc/add-break-details-popup-hoc"; // Import HOC for adding break details
import AddBreakDetailsComponent from "../modal-components/add-break-details-component"; // Import component for adding break details

// Define props interface for EmptyCell component
interface IProps {
  type?: string; // Optional type prop
  key: string; // Key prop
  timeLabel: string; // Time label prop
  day: any; // Day prop
  staffId: number; // Staff ID prop
  staffName: string; // Staff name prop
  staffList: IOption[]; // List of staff options
  disabled?: boolean; // Optional disabled prop
  addBreakDetailsToggleHandler: () => void; // Function to toggle break details popup
  addBreakDetailsPopup: boolean; // Boolean to show/hide break details popup
  openWindow: (params: any) => void; // Function to open a window
  storeId: number; // Store ID prop
}

// Define EmptyCell functional component
const EmptyCell: React.FC<IProps> = (props) => {
  const {
    timeLabel, // Destructure timeLabel from props
    day, // Destructure day from props
    type, // Destructure type from props
    staffId, // Destructure staffId from props
    staffName, // Destructure staffName from props
    staffList, // Destructure staffList from props
    disabled = false, // Destructure disabled from props with default value
    openWindow, // Destructure openWindow from props
    storeId, // Destructure storeId from props
  } = props; // End destructuring

  const { reservationSchedulerStore } = useStore(); // Get reservation scheduler store from context
  const {
    removeDroppedEvent, // Destructure function to remove dropped event
    UpdateAppointmentDraggableService, // Destructure function to update appointment
    UpdateBreakDraggableService, // Destructure function to update break
    getBreaks, // Destructure function to get breaks
    selectedStaffIdWeekly, // Destructure selected staff ID for weekly view
  } = reservationSchedulerStore; // End destructuring

  const currentDate = useMemo(() => localStorage.getItem("currentDate"), []); // Get current date from local storage

  const openStaffBreakPopup = useCallback(() => {
    // Define function to open staff break popup
    props.addBreakDetailsToggleHandler(); // Call toggle handler from props
  }, [props]); // End useCallback

  const handleDropEvent = useCallback(
    // Define function to handle drop event
    (
      toDay: any, // Target day
      toTime: string, // Target time
      data: any, // Dropped data
      dropStaffId: number, // ID of the staff being dropped
      dropStaffName: string, // Name of the staff being dropped
      area: string, // Area where the event is dropped
      dayElem: any // Day element
    ) => {
      const eventDate = moment(data?.Date).format("YYYY-MM-DD"); // Format event date

      if (isCurrentDateGreater(eventDate)) {
        // Check if event date is in the past
        toast.error(formatMessage("cannot_drag_past_events")); // Show error message
        return; // Exit function
      }

      if (area === "Outer") removeDroppedEvent(data.Id); // Remove dropped event if in outer area

      const isWeeklyView = dayElem.props["data-role"] === "weekly"; // Check if view is weekly

      if (data?.EventType === EventTypeEnum.Appointment) {
        // Check if event type is appointment
        if (data?.AppointmentStatus === bookingStatusEnum.Complete) {
          // Check if appointment is complete
          toast.error(formatMessage("cannot_drag_completed_appointment")); // Show error message
          return; // Exit function
        }

        const targetDate = isWeeklyView // Determine target date based on view
          ? moment(dayElem.props["data-index"]).format("YYYY-MM-DD") // For weekly view
          : currentDate; // For daily view

        // Check overlapping appointments only for daily view if needed
        if (!isWeeklyView) {
          const endTime = moment(toTime, "HH:mm") // Calculate end time
            .add(data?.TotalTime + data?.PostBreakTime, "minutes") // Add total time and post break time
            .format("HH:mm"); // Format end time

          const overlapping = getOverlappingBreaksByRange(getBreaks, {
            // Get overlapping breaks
            date: currentDate, // Current date
            startTime: toTime, // Start time
            endTime, // End time
          });

          // Uncomment if overlapping restriction is needed
          // if (overlapping?.length > 0) {
          //   toast.error(formatMessage("cannot_drag_overlapping_appointment")); // Show error message
          //   return; // Exit function
          // }
        }

        UpdateAppointmentDraggableService({
          // Update appointment with new details
          Id: data.Id, // Appointment ID
          // Assigned staff ID based on area and view
          AssignedStaffId:
            area === "Outer" && isWeeklyView
              ? Number(selectedStaffIdWeekly) // Use selected staff ID for weekly view
              : Number(staffId), // Use current staff ID
          Date: `${targetDate}T00:00:00.000Z`, // Target date
          Time: `${toTime}:00`, // Target time
        });
      } else if (data?.EventType === EventTypeEnum.StaffBreak) {
        // Check if event type is staff break
        const start = moment(toTime, "HH:mm"); // Get start time
        const end = start.clone().add(data?.TotalTime, "minutes"); // Calculate end time

        UpdateBreakDraggableService({
          // Update break with new details
          Id: data.BreakID, // Break ID
          StaffId: Number(staffId), // Staff ID
          Date: currentDate, // Current date
          StartTime: start.format("HH:mm:ss"), // Start time
          EndTime: end.format("HH:mm:ss"), // End time
          Title: data.Title, // Title of the break
          Remarks: data.Remarks, // Remarks for the break
        });
      }
    },
    [
      currentDate, // Dependencies for useCallback
      getBreaks, // Dependencies for useCallback
      removeDroppedEvent, // Dependencies for useCallback
      UpdateAppointmentDraggableService, // Dependencies for useCallback
      UpdateBreakDraggableService, // Dependencies for useCallback
      selectedStaffIdWeekly, // Dependencies for useCallback
      staffId, // Dependencies for useCallback
    ]
  ); // End useCallback

  return (
    // Render component
    <>
      {props.addBreakDetailsPopup && ( // Check if break details popup should be shown
        <AddBreakDetailsComponent // Render AddBreakDetailsComponent
          id={-1} // ID for the component
          day={day} // Pass day prop
          staffId={staffId} // Pass staff ID prop
          staffList={staffList} // Pass staff list prop
          startTime={timeLabel} // Pass start time prop
          onModalClosed={props.addBreakDetailsToggleHandler} // Pass handler for closing modal
          storeId={storeId} // Pass store ID prop
        />
      )}

      {!disabled && ( // Check if component is not disabled
        <DroppableCell // Render DroppableCell component
          key={props.key} // Pass key prop
          day={day} // Pass day prop
          time={timeLabel} // Pass time prop
          staffId={staffId} // Pass staff ID prop
          staffName={staffName} // Pass staff name prop
          type={props.type} // Pass type prop
          openBreakModal={openStaffBreakPopup} // Pass function to open break modal
          openReservationWindow={openWindow} // Pass function to open reservation window
          onDropEvent={handleDropEvent} // Pass function to handle drop event
        >
          <></>
        </DroppableCell>
      )}
    </>
  ); // End return
};

// Export the component wrapped with HOC and observer
export default AddBreakDetailsPopupHOC(observer(EmptyCell));
